import { HTTPMethod } from '@/constants/rest';
import merge from 'deepmerge';
import qs from 'query-string';
import { localStorageClient } from './dom';

export type SearchParams = Record<string, unknown>;

export type FetchArgs = Omit<RequestInit, 'body' | 'method'> & {
	body?: Record<string, unknown> | FormData | string;
	toBlob?: boolean;
	method?: HTTPMethod;
	searchParams?: SearchParams;
};

export type ApiErrorItem = {
	message: string;
	severity?: string;
	attribute?: string;
	code?: string;
};

export interface FetchResponse {
	status: number;
	headers?: Headers;
}

export interface FetchJsonResponse<T> extends FetchResponse {
	json?: T;
}

export interface FetchBlobResponse extends FetchResponse {
	blob?: Blob;
}

export interface Fetcher {
	(path: string, args: FetchArgs & { toBlob: true }): Promise<FetchBlobResponse>;
	<Json>(path: string, args?: FetchArgs & { toBlob?: false }): Promise<FetchJsonResponse<Json>>;
}

export class FetcherError extends Error {
	statusCode: number;

	constructor(
		public readonly path: string,
		public readonly response: FetchJsonResponse<{
			code?: string;
			message?: string[];
			errors?: ApiErrorItem[];
		}>
	) {
		super(FetcherError.transformErrorMessage(response));
		this.statusCode = response.status;
	}

	public static transformErrorMessage(
		response: FetchJsonResponse<{ code?: string; message?: string[] }>
	) {
		const { message, code } = response.json || {};

		if (Array.isArray(message)) {
			return message.join(', ');
		}

		return code ? `Error Code: ${code}` : 'Fetcher Error';
	}
}

export function isFetcherError(error: unknown): error is FetcherError {
	return error instanceof FetcherError;
}

export function getFetcherErrorItems(error: unknown): ApiErrorItem[] {
	if (!isFetcherError(error)) {
		return [];
	}

	const responseJson = error.response.json as unknown;

	if (
		responseJson &&
		typeof responseJson === 'object' &&
		'errors' in responseJson &&
		Array.isArray(responseJson.errors)
	) {
		return responseJson.errors.filter(
			(item): item is ApiErrorItem =>
				!!item && typeof item === 'object' && 'message' in item && typeof item.message === 'string'
		);
	}

	return [];
}

export function getFetcherErrorMessages(error: unknown): string[] {
	if (!isFetcherError(error)) {
		return [];
	}

	const responseJson = error.response.json as unknown;
	const messages = new Set<string>();

	for (const item of getFetcherErrorItems(error)) {
		messages.add(item.message);
	}

	if (typeof responseJson === 'string') {
		messages.add(responseJson);
	}

	if (responseJson && typeof responseJson === 'object' && 'message' in responseJson) {
		if (typeof responseJson.message === 'string') {
			messages.add(responseJson.message);
		}

		if (Array.isArray(responseJson.message)) {
			for (const message of responseJson.message) {
				if (typeof message === 'string') {
					messages.add(message);
				}
			}
		}
	}

	return Array.from(messages);
}

export function getFetcherErrorMessage(error: unknown): string | undefined {
	return getFetcherErrorMessages(error)[0];
}

function getSavedLocale() {
	return localStorageClient.getItem('locale')?.toLowerCase();
}

export function getApiLocale(): string {
	const savedLocale = getSavedLocale();
	switch (savedLocale) {
		default:
		case 'cs':
			return 'CZ';
	}
}

const getClientVersion = () => {
	const version = process.env.npm_package_version;
	const system = process.env.BUILD_PLATFORM;

	return `${system || 'web'}/${version || '0.0.0'}`;
};

const randomToken = () => Math.random().toString(36).substr(2, 9);

export function addSearchParams(url: string, args: SearchParams = {}): string {
	const { query: initialQuery, url: baseUrl } = qs.parseUrl(url);

	const queryObj = merge(initialQuery, args);
	const querystring = qs.stringify(queryObj, { skipNull: true });
	return querystring.length > 0 ? `${baseUrl}?${querystring}` : baseUrl;
}

type Options = {
	sendContentType?: boolean;
	parseResponsesAsJson?: boolean;
};

export function createFetcher(baseUrl: string, fetchOptions?: Options): Fetcher {
	async function fetchCall<T = unknown | Blob>(
		path: string,
		fetchArgs?: FetchArgs
	): Promise<FetchJsonResponse<T> | FetchBlobResponse> {
		const body = fetchArgs?.body;
		const method = fetchArgs?.method || HTTPMethod.GET;

		const transactionId = randomToken();
		const clientVersion = getClientVersion();

		const options: RequestInit = {
			...fetchArgs,
			body: undefined,
			headers: {
				Accept: 'application/json',
				'Accept-Language': getApiLocale(),
				...((global.FormData != null && body instanceof FormData) || !fetchOptions?.sendContentType
					? undefined
					: { 'Content-Type': 'application/json' }),

				'X-Transaction-ID': transactionId,
				'x-client-version': clientVersion,
				'X-Language': process.env.NEXT_PUBLIC_LANGUAGE,

				...(fetchArgs?.headers || {}),
			},
		};

		const canIncludeBody = ![HTTPMethod.GET, HTTPMethod.HEAD].includes(method);
		if (canIncludeBody) {
			if (global.FormData != null && body instanceof FormData) {
				options.body = body;
			} else {
				options.body = body !== Object(body) ? String(body) : JSON.stringify(body);
			}
		}

		const url = addSearchParams(`${baseUrl}${path}`, fetchArgs?.searchParams);
		const result = await fetch(url, options);

		const contentType = result.headers?.get('Content-Type') || '';
		const isJSON = fetchOptions?.parseResponsesAsJson ?? contentType.includes('application/json');
		const isXML = contentType.includes('text/xml');

		if (!result.ok) {
			throw new FetcherError(path, {
				status: result.status,
				headers: result.headers,
				json: isJSON ? await result.json() : null,
			});
		}

		if (fetchArgs?.toBlob) {
			const response: FetchBlobResponse = {
				status: result.status,
				headers: result.headers,
				blob: await result.blob(),
			};

			return response;
		} else {
			const response: FetchJsonResponse<T> = {
				status: result.status,
				headers: result.headers,
				json: isJSON ? await result.json() : isXML ? await result.text() : null,
			};

			return response;
		}
	}

	return fetchCall;
}
